import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, CircularProgress, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { articuloPorId } from '../data/ayuda/articulos';
import BreadcrumbManual from '../components/help/BreadcrumbManual';
import MarkdownRenderer from '../components/help/MarkdownRenderer';
import ArticulosRelacionados from '../components/help/ArticulosRelacionados';
import FeedbackArticulo from '../components/help/FeedbackArticulo';

const API = process.env.REACT_APP_API_URL || '';

export default function ArticuloManual() {
  const { articuloId } = useParams();
  const navigate = useNavigate();
  const [contenido, setContenido] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const articulo = articuloPorId(articuloId);

  useEffect(() => {
    if (!articulo) {
      setCargando(false);
      setError('Artículo no encontrado.');
      return;
    }
    setCargando(true);
    setError(null);
    fetch(`${API}${articulo.archivo}`)
      .then(r => {
        if (!r.ok) throw new Error('No disponible');
        return r.text();
      })
      .then(txt => setContenido(txt))
      .catch(() => setError('El contenido de este artículo aún no está disponible.'))
      .finally(() => setCargando(false));
  }, [articuloId, articulo]);

  if (!articulo && !cargando) {
    return (
      <Box sx={{ maxWidth: 680 }}>
        <BreadcrumbManual />
        <Typography sx={{ color: '#ef9a9a', mt: 4 }}>Artículo no encontrado.</Typography>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate('/manual')}
          sx={{ mt: 2, color: 'var(--c-acento)', textTransform: 'none', fontSize: 13 }}
        >
          Volver al manual
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 680 }}>
      {articulo && (
        <BreadcrumbManual rolArticulo={articulo.rol} tituloArticulo={articulo.titulo} />
      )}

      {articulo && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#e0e0e0', mb: 1 }}>
            {articulo.titulo}
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#90a4ae', mb: 2 }}>
            {articulo.resumen}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 14, color: '#546e7a' }} />
              <Typography sx={{ fontSize: 12, color: '#546e7a' }}>
                {articulo.tiempoLectura} min de lectura
              </Typography>
            </Box>
            {articulo.versionSistema && (
              <Chip
                label={`v${articulo.versionSistema}`}
                size="small"
                sx={{ fontSize: 11, height: 20, color: '#546e7a', bgcolor: 'rgba(84,110,122,0.15)', border: 'none' }}
              />
            )}
            {articulo.ultimaRevision && (
              <Typography sx={{ fontSize: 11, color: '#3d5a6e' }}>
                Actualizado: {articulo.ultimaRevision}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Contenido */}
      <Box sx={{
        bgcolor: 'var(--c-superficie)', borderRadius: 2,
        border: '1px solid rgba(255,255,255,0.06)',
        p: { xs: 2, sm: 3 }, minHeight: 200,
      }}>
        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: 'var(--c-acento)' }} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ fontSize: 32, mb: 2 }}>🔧</Typography>
            <Typography sx={{ color: '#90a4ae', fontSize: 14 }}>{error}</Typography>
          </Box>
        ) : (
          <MarkdownRenderer>{contenido}</MarkdownRenderer>
        )}
      </Box>

      {/* Footer del artículo */}
      {articulo && !cargando && !error && (
        <>
          <FeedbackArticulo articuloId={articuloId} />
          <ArticulosRelacionados relacionados={articulo.relacionados} />
        </>
      )}

      <Box sx={{ mt: 4 }}>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate(-1)}
          sx={{ color: '#546e7a', textTransform: 'none', fontSize: 13,
            '&:hover': { color: 'var(--c-acento)' } }}
        >
          Volver
        </Button>
      </Box>
    </Box>
  );
}
